import moment from "moment";
import { useEffect, useState } from "react";
import {
  createComplaintTopicApi,
  deleteComplaintTopicApi,
  getComplaintTopicsApi,
  updateComplaintTopicApi,
} from "../../api/appeal";
import Breadcrumb from "../../component/breadcrumb";
import Datatable from "../../component/datatable";
import { IComplaintTopic, ICreateComplaintTopic } from "../../interface/appeal";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";

const ComplaintTopics = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState<IComplaintTopic[]>([]);

  // Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<IComplaintTopic | null>(
    null
  );

  const [formData, setFormData] = useState<ICreateComplaintTopic>({
    code: "",
    label: "",
    description: "",
    active: false,
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState<ICreateComplaintTopic>({
    code: "",
    label: "",
    description: "",
    active: false,
  });

  const fetchTopics = async (page = 1) => {
    try {
      setLoading(true);
      const { data } = await getComplaintTopicsApi();
      setTopics(data ?? []);
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Something went wrong.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await createComplaintTopicApi(formData);
      setFormData({
        code: "",
        label: "",
        description: "",
        active: false,
      });
      await fetchTopics();
      openSnackbar("Topic created successfully");
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Failed to create topic.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: string, data: ICreateComplaintTopic) => {
    try {
      setLoading(true);
      await updateComplaintTopicApi(id, { ...data, id });
      await fetchTopics();
      openSnackbar("Topic updated successfully");
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Failed to update topic.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this topic?")) {
      return;
    }

    try {
      setLoading(true);
      await deleteComplaintTopicApi(id);
      await fetchTopics();
      openSnackbar("Topic deleted successfully");
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Failed to delete topic.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    const topic = topics.find((t) => t.id === id);
    if (topic) {
      setEditingTopic(topic);
      setEditFormData({
        code: topic.code,
        label: topic.label,
        description: topic.description,
        active: topic.active,
      });
      setIsEditModalOpen(true);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTopic) return;

    try {
      setLoading(true);
      await updateComplaintTopicApi(editingTopic.id, {
        ...editFormData,
        id: editingTopic.id,
      });
      await fetchTopics();
      setIsEditModalOpen(false);
      setEditingTopic(null);
      openSnackbar("Topic updated successfully");
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Failed to update topic.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTopic(null);
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    dispatch(setAppLoading(loading));
  }, [loading, dispatch]);

  // Make handleEdit available globally for the datatable
  useEffect(() => {
    (window as any).handleEdit = handleEdit;
    (window as any).handleDelete = handleDelete;
  }, [topics]);

  return (
    <>
      <Breadcrumb
        title={"หัวข้อร้องเรียน"}
        children={[
          { title: "หน้าแรก", link: "/" },
          { title: "ระบบร้องเรียน", link: "/complaints" },
          { title: "หัวข้อร้องเรียน" },
        ]}
      />

      <div className="row">
        <div className="col-12 mb-3">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">เพิ่มหัวข้อร้องเรียน</h4>
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>รหัส</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="เช่น DRIVER_RUDE"
                        value={formData.code}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            code: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>ชื่อหัวข้อ</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="เช่น คนขับพูดจาไม่สุภาพ"
                        value={formData.label}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            label: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="form-group">
                      <label>รายละเอียด</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="รายละเอียดเพิ่มเติม"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <button type="submit" className="btn btn-primary">
                      เพิ่มหัวข้อ
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-4">รายการหัวข้อร้องเรียน</h4>
              <Datatable
                columns={[
                  {
                    title: "รหัส",
                    data: "code",
                    width: 150,
                  },
                  {
                    title: "ชื่อหัวข้อ",
                    data: "label",
                    width: 200,
                  },
                  {
                    title: "รายละเอียด",
                    data: "description",
                    width: 300,
                  },
                  {
                    title: "สถานะ",
                    data: "active",
                    width: 100,
                    render(data: boolean) {
                      return data
                        ? '<span class="badge badge-success">เปิดใช้งาน</span>'
                        : '<span class="badge badge-danger">ปิดใช้งาน</span>';
                    },
                  },
                  {
                    title: "วันที่สร้าง",
                    data: "createdAt",
                    width: 150,
                    render(data: string) {
                      return moment(data).format("DD/MM/YYYY HH:mm");
                    },
                  },
                  {
                    title: "การจัดการ",
                    data: null,
                    width: 200,
                    render(data: any, type: any, row: IComplaintTopic) {
                      return `
                        <div class="d-flex">
                          <button class="btn btn-info btn-sm mr-2" onclick="handleEdit('${row.id}')">
                            แก้ไข
                          </button>
                          <button class="btn btn-danger btn-sm" onclick="handleDelete('${row.id}')">
                            ลบ
                          </button>
                        </div>
                      `;
                    },
                  },
                ]}
                data={topics}
              />

              {/* <Pagination
                active={pagination.page}
                limit={pagination.limit}
                onPageChange={fetchTopics}
                pageRangeDisplayed={5}
                total={pagination.total}
              /> */}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div
        className={`modal fade ${isEditModalOpen ? "show" : ""}`}
        style={{ display: isEditModalOpen ? "block" : "none" }}
        tabIndex={-1}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">แก้ไขหัวข้อร้องเรียน</h5>
              <button type="button" className="close" onClick={closeEditModal}>
                <span>&times;</span>
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>รหัส</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="เช่น DRIVER_RUDE"
                        value={editFormData.code}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            code: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>ชื่อหัวข้อ</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="เช่น คนขับพูดจาไม่สุภาพ"
                        value={editFormData.label}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            label: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <div className="form-group">
                      <label>รายละเอียด</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="รายละเอียดเพิ่มเติม"
                        value={editFormData.description}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <div className="form-group">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="editIsActive"
                          checked={editFormData.active}
                          onChange={(e) =>
                            setEditFormData((prev) => ({
                              ...prev,
                              active: e.target.checked,
                            }))
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="editIsActive"
                        >
                          เปิดใช้งาน
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEditModal}
                >
                  ยกเลิก
                </button>
                <button type="submit" className="btn btn-primary">
                  บันทึกการแก้ไข
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal backdrop */}
      {isEditModalOpen && (
        <div className="modal-backdrop fade show" onClick={closeEditModal} />
      )}
    </>
  );
};

export default ComplaintTopics;
